/**
 * The submission's strength, shown while it is being built.
 *
 * This is the whole reason the Submission Portal is a different product from
 * COMM-PASS. The same A+…F score has existed for a while, computed from the
 * submission's own data — but only staff have ever seen it. An agent found out
 * their submission was thin when a specialist told them, or when a carrier
 * didn't respond.
 *
 * ⚠️ MEMBERS SEE THE LETTER, NEVER THE NUMBER (Josh, 2026-08-30). A visible
 * points economy teaches people to farm points instead of building a submission
 * — a member who knows loss runs are "worth 6" starts asking what else is worth
 * 6, which is the wrong question. "A+" is a verdict they can act on; "96 / 100"
 * invites "what is the other 4?". Staff see both, because they triage on it.
 *
 * So the gaps were always the point, not the letter. "B-" tells an agent nothing
 * they can act on; "No loss runs" tells them exactly what to go and get.
 *
 * The score comes from the server on every save, never recomputed here — a
 * grade that disagreed with the one AAS sees would be worse than none.
 */
import { MIN_SEND_SCORE, type SubmissionGrade } from '@commpass/shared';

export function GradePanel({
  grade,
  saving,
  /** Staff see the actual grade. Members see readiness and next steps. */
  reveal = false,
}: {
  grade: SubmissionGrade | null;
  saving?: boolean;
  reveal?: boolean;
}) {
  if (!grade) return null;

  const ready = grade.score >= MIN_SEND_SCORE;

  return (
    <aside className={`grade-panel is-${grade.band}`}>
      {/* The letter is shown to everyone (Josh, 2026-08-30). The raw score is
          not: "A+" is a verdict you can act on, while "96 / 100" invites the
          question "what's the other 4?" and turns building a submission into
          farming one. That distinction is the whole reason the per-document
          point values are still hidden too. */}
      <div className="grade-head">
        <div className="grade-letter">{grade.grade}</div>
        <div>
          <div className="grade-label">Submission strength</div>
          {reveal ? (
            <div className="grade-score">
              {grade.score} / 100
              {saving && <span className="grade-saving"> · saving…</span>}
            </div>
          ) : (
            <div className="grade-state">
              {ready ? 'Ready to send' : 'Not ready to send yet'}
              {saving && <span className="grade-saving"> · saving…</span>}
            </div>
          )}
        </div>
      </div>

      {/* The bar moves without ever naming a number, so progress is visible and
          not scoreable. */}
      <div className="grade-bar" aria-hidden="true">
        <span style={{ width: `${Math.max(2, grade.score)}%` }} />
      </div>

      {/* Gaps first and always. Someone opening this wants to know what to go
          and fetch, not to admire a letter. They are already in impact order —
          documents, then profile, then narrative. */}
      {grade.gaps.length > 0 && (
        <div className="grade-block">
          <h4>{ready ? 'What would make it stronger' : 'Do these next'}</h4>
          <ul className="grade-gaps">
            {grade.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      {grade.boosts.length > 0 && (
        <div className="grade-block">
          <h4>Working in your favour</h4>
          <ul className="grade-boosts">
            {grade.boosts.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Said plainly, because a strength rating with no stated meaning invites
          people to invent one — and the one they invent is usually "will you
          decline me". */}
      <p className="grade-note">
        This measures how complete the submission is, not whether the risk is any good. A
        well-documented account gets read first and quoted faster.
      </p>
    </aside>
  );
}
